<template>
  <v-menu
    v-model="menu"
    :close-on-content-click="false"
    :open-on-click="false"
    :open-on-focus="false"
    transition="scale-transition"
    offset-y
    min-width="auto"
    :disabled="!showPicker"
  >
    <template v-slot:activator="{ attrs }">
      <v-text-field
        ref="input"
        v-model="value"
        v-bind="attrs"
        :label="translatedLabel"
        :name="name || label"
        :hint="hintText"
        :placeholder="placeholderText"
        :append-icon="appendIcon"
        inputmode="numeric"
        maxlength="10"
        autocorrect="off"
        autocapitalize="off"
        spellcheck="false"
        @input.native="onNativeInput"
        @focus="onFocus"
        @blur="onBlur"
        @keydown.alt.down.prevent="openPicker"
        @keydown.esc="menu = false"
        @click:append="openPicker"
        @click:clear="onClear"
        autocomplete="ansdoasdjosk"
        :rules="validationRules"
        :error-messages="errorMessages"
        :clearable="clearable"
        :success="showSuccess"
        :error="showError"
        :color="isFocused ? 'primary' : undefined"
        :aria-label="translatedLabel"
        :aria-invalid="showError ? 'true' : 'false'"
        :aria-required="obrigatorio ? 'true' : 'false'"
        validate-on-blur
        hide-details="auto"
        class="
          input-style
          font-size-input
          text-light-input
          placeholder-light
          input-icon
        "
        dense
        flat
        filled
        solo
        :disabled="disabled"
      >
      </v-text-field>
    </template>
    <div
      @mousedown.capture="onMenuPointerDown"
      @pointerdown.capture="onMenuPointerDown"
    >
      <v-date-picker
        v-model="pickerDate"
        :locale="pickerLocale"
        @input="onPickerDate"
      />
    </div>
  </v-menu>
</template>

<script>
import { translateIfMessageExists } from "@/utils/i18n";

export default {
  name: "CampoData",

  props: {
    label: { type: String, default: "Data" },
    payload: { type: String, required: false },
    name: { type: String, required: false },
    formato: { type: String, default: "AUTO" },
    clearable: { type: Boolean, default: true },
    obrigatorio: { type: Boolean, default: false },
    showPicker: { type: Boolean, default: true },
    anoMinimo: { type: Number, default: 1900 },
    anoMaximo: { type: Number, default: 2100 },
    disabled: {
      type: Boolean,
      default: false,
      required: false,
    },
  },

  mounted() {
    setTimeout(() => {
      this.setFromPayload(this.payload);
    }, 700);
  },

  watch: {
    payload(val) {
      this.setFromPayload(val);
    },
    menu(val) {
      if (val) {
        this.syncPickerFromValue();
      }
    },
  },

  data() {
    return {
      value: "",
      menu: false,
      pickerDate: null,
      isFocused: false,
      ignoreBlur: false,
    };
  },

  methods: {
    onFocus() {
      this.isFocused = true;
    },

    onBlur() {
      this.isFocused = false;
      if (this.ignoreBlur) {
        this.ignoreBlur = false;
        return;
      }
      const applied = this.applyShortcut();
      if (!applied && /[a-zA-Z]/.test(this.value || "")) {
        this.value = "";
      }
      this.menu = false;
      this.$emit("date", this.formatedValue);
    },

    openPicker() {
      if (this.showPicker && !this.disabled) {
        this.menu = true;
      }
    },

    onMenuPointerDown() {
      this.ignoreBlur = true;
    },

    onPickerDate(value) {
      if (!value) {
        return;
      }
      const parts = this.parseIsoDate(value);
      if (!parts) {
        return;
      }
      this.value = this.formatDisplayFromParts(parts);
      this.menu = false;
      this.$emit("date", this.formatedValue);
    },

    onClear() {
      this.value = "";
      this.pickerDate = null;
      this.menu = false;
      this.$emit("date", null);
    },

    setFromPayload(payload) {
      if (!payload) {
        this.value = "";
        return;
      }
      const isoParts = this.parseIsoDate(payload);
      if (isoParts) {
        this.value = this.formatDisplayFromParts(isoParts);
        return;
      }
      if (payload.includes("/")) {
        if (this.isMdy && payload.length >= 10) {
          const parts = this.parseDisplayParts(payload, false);
          if (parts) {
            this.value = this.formatDisplayFromParts(parts);
            return;
          }
        }
        this.value = payload;
        return;
      }
      if (this.$options.filters && this.$options.filters.dataParaLeitura) {
        this.value = this.$options.filters.dataParaLeitura(payload);
      } else {
        this.value = payload;
      }
    },

    syncPickerFromValue() {
      const iso = this.displayToIso(this.value);
      this.pickerDate = iso && iso.length >= 10 ? iso.substring(0, 10) : null;
    },

    onNativeInput(event) {
      const raw = (event && event.target && event.target.value) || "";
      const selectionStart =
        event && event.target ? event.target.selectionStart : null;
      const isoParts = this.parseIsoDate(raw);
      if (isoParts) {
        const normalized = this.normalizeIsoParts(isoParts);
        const formatted = this.formatDisplayFromParts(normalized);
        this.value = formatted;
        this.setCaret(formatted.length);
        return;
      }
      const trimmed = String(raw || "").trim();
      if (/[a-zA-Z]/.test(trimmed) || /^[+-]/.test(trimmed)) {
        this.value = raw;
        return;
      }
      const { formatted, caret } = this.applyMask(raw, selectionStart);
      this.value = formatted;
      if (caret !== null) {
        this.setCaret(caret);
      }
    },

    applyMask(raw, selectionStart) {
      const digits = this.onlyDigits(raw).slice(0, 8);
      const sanitized = this.sanitizeDateDigits(digits);
      const formatted = this.formatWithPattern(sanitized, "##/##/####");
      if (typeof selectionStart !== "number") {
        return { formatted, caret: null };
      }
      const digitsBeforeCaret = this.onlyDigits(
        raw.slice(0, selectionStart)
      ).length;
      const preferSeparator =
        selectionStart > 0 &&
        raw[selectionStart - 1] &&
        !/\d/.test(raw[selectionStart - 1]);
      const caret = this.caretFromDigits(
        formatted,
        digitsBeforeCaret,
        preferSeparator
      );
      return { formatted, caret };
    },

    sanitizeDateDigits(digits) {
      if (!digits) {
        return "";
      }
      const first = digits.slice(0, 2);
      const second = digits.slice(2, 4);
      const year = digits.slice(4, 8);

      let day = this.isMdy ? second : first;
      let month = this.isMdy ? first : second;

      month = this.clampSegment(month, 12);
      day = this.clampSegment(day, 31);

      if (day.length === 2 && month.length === 2 && year.length === 4) {
        const yearNum = Number(year);
        const monthNum = Number(month);
        if (!Number.isNaN(yearNum) && monthNum >= 1 && monthNum <= 12) {
          const maxDay = new Date(yearNum, monthNum, 0).getDate();
          const dayNum = Number(day);
          if (!Number.isNaN(dayNum)) {
            const adjusted = Math.min(Math.max(dayNum, 1), maxDay);
            day = String(adjusted).padStart(2, "0");
          }
        }
      }

      return this.isMdy ? `${month}${day}${year}` : `${day}${month}${year}`;
    },

    clampSegment(segment, max, min = 1) {
      if (!segment || segment.length < 2) {
        return segment || "";
      }
      let num = Number(segment);
      if (Number.isNaN(num)) {
        return segment;
      }
      if (num < min) {
        num = min;
      }
      if (num > max) {
        num = max;
      }
      return String(num).padStart(2, "0");
    },

    normalizeIsoParts(parts) {
      const yearNum = Number(parts.year);
      let monthNum = Number(parts.month);
      let dayNum = Number(parts.day);

      if (Number.isNaN(monthNum) || monthNum <= 0) {
        monthNum = 1;
      }
      if (monthNum > 12) {
        monthNum = 12;
      }

      if (Number.isNaN(dayNum) || dayNum <= 0) {
        dayNum = 1;
      }
      if (!Number.isNaN(yearNum) && monthNum >= 1 && monthNum <= 12) {
        const maxDay = new Date(yearNum, monthNum, 0).getDate();
        if (dayNum > maxDay) {
          dayNum = maxDay;
        }
      } else if (dayNum > 31) {
        dayNum = 31;
      }

      return {
        year: String(Number.isNaN(yearNum) ? parts.year : yearNum),
        month: this.padZero(monthNum),
        day: this.padZero(dayNum),
      };
    },

    normalizeKeyword(value) {
      return String(value || "")
        .trim()
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "");
    },

    applyShortcut() {
      const rawValue = this.value || "";
      if (!rawValue) {
        return false;
      }
      const normalized = this.normalizeKeyword(rawValue);
      if (!normalized) {
        return false;
      }
      let dayOffset = null;
      if (normalized === "hoje") {
        dayOffset = 0;
      } else if (normalized === "amanha") {
        dayOffset = 1;
      } else if (normalized === "ontem") {
        dayOffset = -1;
      } else {
        const match = normalized.match(/^([+-]?\d+)\s*d$/);
        if (match) {
          dayOffset = Number(match[1]);
        }
      }
      if (dayOffset === null || Number.isNaN(dayOffset)) {
        return false;
      }
      const base = new Date();
      base.setHours(0, 0, 0, 0);
      base.setDate(base.getDate() + dayOffset);
      this.value = this.formatDisplayFromDate(base);
      return true;
    },

    parseIsoDate(raw) {
      const match = String(raw || "").match(/(\d{4})-(\d{2})-(\d{2})/);
      if (!match) {
        return null;
      }
      return { year: match[1], month: match[2], day: match[3] };
    },

    parseDisplayParts(value, allowMdy = true) {
      const parts = String(value || "").split("/");
      if (parts.length < 3) {
        return null;
      }
      const first = parts[0];
      const second = parts[1];
      const third = parts[2];
      if (this.isMdy && allowMdy) {
        return { day: second, month: first, year: third };
      }
      return { day: first, month: second, year: third };
    },

    formatDisplayFromParts({ day, month, year }) {
      const safeDay = this.padZero(day);
      const safeMonth = this.padZero(month);
      if (this.isMdy) {
        return `${safeMonth}/${safeDay}/${year}`;
      }
      return `${safeDay}/${safeMonth}/${year}`;
    },

    formatDisplayFromDate(dateObj) {
      const day = this.padZero(dateObj.getDate());
      const month = this.padZero(dateObj.getMonth() + 1);
      const year = String(dateObj.getFullYear());
      return this.formatDisplayFromParts({ day, month, year });
    },

    displayToIso(value) {
      if (!value || value.length < 10) {
        return null;
      }
      const parts = this.parseDisplayParts(value);
      if (!parts) {
        return null;
      }
      const month = this.padZero(parts.month);
      const day = this.padZero(parts.day);
      return `${parts.year}-${month}-${day}`;
    },

    isValidDate(value) {
      if (!value || value.length < 10) {
        return false;
      }
      const parts = this.parseDisplayParts(value);
      if (!parts) {
        return false;
      }
      const year = Number(parts.year);
      const month = Number(parts.month);
      const day = Number(parts.day);
      if (!year || !month || !day) {
        return false;
      }
      if (this.anoMinimo && year < this.anoMinimo) {
        return false;
      }
      if (this.anoMaximo && year > this.anoMaximo) {
        return false;
      }
      const dateObj = new Date(year, month - 1, day);
      return (
        dateObj.getFullYear() === year &&
        dateObj.getMonth() === month - 1 &&
        dateObj.getDate() === day
      );
    },

    padZero(value) {
      return String(value).padStart(2, "0");
    },

    onlyDigits(value) {
      return String(value || "").replace(/\D/g, "");
    },

    formatWithPattern(digits, pattern) {
      if (!digits) {
        return "";
      }
      let result = "";
      let digitIndex = 0;
      for (let i = 0; i < pattern.length; i++) {
        const char = pattern[i];
        if (char === "#") {
          if (digitIndex < digits.length) {
            result += digits[digitIndex];
            digitIndex += 1;
          } else {
            break;
          }
        } else if (digitIndex < digits.length) {
          result += char;
        }
      }
      return result;
    },

    caretFromDigits(formatted, digitsCount, preferSeparator) {
      if (!formatted || digitsCount <= 0) {
        return 0;
      }
      let count = 0;
      for (let i = 0; i < formatted.length; i++) {
        if (/\d/.test(formatted[i])) {
          count += 1;
          if (count === digitsCount) {
            if (
              preferSeparator &&
              formatted[i + 1] &&
              /\D/.test(formatted[i + 1])
            ) {
              return i + 2;
            }
            return i + 1;
          }
        }
      }
      return formatted.length;
    },

    getInputEl() {
      if (!this.$refs.input || !this.$refs.input.$el) {
        return null;
      }
      return this.$refs.input.$el.querySelector("input");
    },

    setCaret(position) {
      this.$nextTick(() => {
        const input = this.getInputEl();
        if (input && typeof input.setSelectionRange === "function") {
          input.setSelectionRange(position, position);
        }
      });
    },
  },

  computed: {
    isMdy() {
      const format = String(this.formato || "").toUpperCase();
      if (format === "AUTO") {
        const locale = String(this.pickerLocale || "").toLowerCase();
        return locale.startsWith("en") || locale.startsWith("us");
      }
      return format === "MDY";
    },

    translatedLabel() {
      return translateIfMessageExists(this.$i18n, this.label);
    },

    hintText() {
      return this.isMdy ? "MM/DD/AAAA" : "DD/MM/AAAA";
    },

    placeholderText() {
      if (!this.isFocused && this.value) {
        return "";
      }
      return this.hintText;
    },

    appendIcon() {
      return this.showPicker ? "mdi-calendar" : undefined;
    },

    pickerLocale() {
      return this.$i18n && this.$i18n.locale ? this.$i18n.locale : "pt-BR";
    },

    showSuccess() {
      return (
        !!this.value && this.value.length === 10 && this.isValidDate(this.value)
      );
    },

    showError() {
      return (
        !!this.value &&
        this.value.length === 10 &&
        !this.isValidDate(this.value)
      );
    },

    errorMessages() {
      if (!this.value || this.value.length !== 10) {
        return [];
      }
      return this.isValidDate(this.value) ? [] : this.$t("Data_invalida");
    },

    validationRules() {
      return this.obrigatorio
        ? [(value) => !!value || this.$t("Campo_obrigatorio")]
        : [];
    },

    formatedValue: function () {
      const data = this.value;
      if (!data) {
        return null;
      }
      if (data.length !== 10) {
        return null;
      }
      if (!this.isValidDate(data)) {
        return null;
      }
      const iso = this.displayToIso(data);
      return iso || data;
    },
  },
};
</script>

<style></style>
